import React from 'react';
import { action, computed } from 'mobx';
import { observer } from 'mobx-react';
import { HostMap } from 'services/pangolin/types';
import { hostMatchesSearchString } from 'services/pangolin/hosts_service';
import { ButtonClickEvent } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { SearchInput } from 'ui/standard/input/search_input/search_input';
import baseStyles from './device_input.module.css';
import { DeviceInputStore } from './device_input_store';
import { RemovableDeviceEntry, SearchDeviceEntry, SearchDeviceEntryProps } from 'ui/issues/device_entry';
import { DeviceEntry, getDeviceEntryID, hostToDeviceEntry } from 'ui/issues/device_entry/device_entry_logic';

type DeviceInputStyles = {
  inputButton: string;
  placeholder: string;
  plusIcon: string;
};

const themedStyles: ThemedStyles<DeviceInputStyles> = {
  [Theme.DAYLIGHT]: {
    inputButton: baseStyles.daylightInputButton,
    placeholder: baseStyles.daylightPlaceholder,
    plusIcon: baseStyles.daylightPlusIcon,
  },
  [Theme.MIDNIGHT]: {
    inputButton: baseStyles.midnightInputButton,
    placeholder: baseStyles.midnightPlaceholder,
    plusIcon: baseStyles.midnightPlusIcon,
  },
};

type DeviceEntryDisplayProps = {
  store: DeviceInputStore;
  entry: DeviceEntry;
};

const DeviceEntryDisplay = observer(({ store, entry }: DeviceEntryDisplayProps) => {
  const onRemoveClick = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.removeEntry(entry);
  }), [store, entry]);
  return <RemovableDeviceEntry entry={entry} onRemoveClick={onRemoveClick}/>;
});

type DeviceInputDisplayProps = {
  store: DeviceInputStore;
  placeholder: string;
};

const DeviceInputDisplay = observer(({ store, placeholder }: DeviceInputDisplayProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.search.search.setValue('');
    store.search.modal.showModal();
  }), [store]);

  return (
    <>
      <button type='button' className={styles.inputButton} onClick={onShowModal}>
        <div className={styles.placeholder}>
          {placeholder}
        </div>
        <div className={styles.plusIcon}/>
      </button>
      {store.entries.map((entry) =>
        <DeviceEntryDisplay store={store} entry={entry} key={getDeviceEntryID(entry)}/>)}
    </>
  );
});

type DeviceInputProps = {
  placeholder: string;
  hosts: HostMap;
  store: DeviceInputStore;
};

@observer export class DeviceInput extends React.Component<DeviceInputProps> {
  @computed get entries(): DeviceEntry[] {
    const entries: DeviceEntry[] = [];
    this.props.hosts.forEach((host) => {
      const entry = hostToDeviceEntry(host);
      const id = getDeviceEntryID(entry);
      if (!this.currentEntryIDs.has(id)) {
        entries.push(entry);
      }
    });
    return entries;
  }

  @computed get currentEntryIDs(): Map<string, true> {
    const map: Map<string, true> = new Map();
    const ids = this.props.store.entries.map(getDeviceEntryID);
    ids.forEach((id) => map.set(id, true));
    return map;
  }

  filterEntry = (item: DeviceEntry, text: string): boolean => {
    return hostMatchesSearchString(item.host, text);
  };

  DisplayComponent = () => {
    return <DeviceInputDisplay store={this.props.store} placeholder={this.props.placeholder} />;
  };

  ItemComponent = ({ data: entry, ...restProps }: Omit<SearchDeviceEntryProps, 'onSelect'>) => {
    const onSelect = React.useCallback(action(() => {
      this.props.store.commitEntry(entry);
      this.props.store.search.modal.hideModal();
    }), [this.props.store, entry]);
    return <SearchDeviceEntry data={entry} onSelect={onSelect} {...restProps}/>;
  };

  render() {
    return <SearchInput
      selectOnly={false}
      placeholder={this.props.placeholder}
      store={this.props.store.search}
      data={this.entries}
      filterFunc={this.filterEntry}
      keyFunc={getDeviceEntryID}
      ItemComponent={this.ItemComponent}
      DisplayComponent={this.DisplayComponent}
    />;
  }
}
